/* Progressive enhancement: content remains visible if JS or observers are unavailable. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const played = new WeakSet();
  const running = new Set();
  const effects = new WeakMap();
  const easing = 'cubic-bezier(.22,.61,.36,1)';
  const desktop = matchMedia('(min-width:750px)');
  let observer;

  function animate(element, frames, options = {}) {
    if (!element || reduced.matches || !element.animate) return;
    const animation = element.animate(frames, { duration:480, easing, ...options });
    running.add(animation);
    animation.onfinish = animation.oncancel = () => running.delete(animation);
  }

  function rise(element, delay = 0, x = 0) {
    animate(element, [
      { opacity:.12, transform:`translate(${x}px, ${x ? 10 : 24}px)` },
      { opacity:1, transform:'none' }
    ], { duration:720, delay, fill:'backwards' });
  }

  function revealPhoto(element, delay = 0, x = 0) {
    animate(element, [
      { opacity:.3, clipPath:'inset(0 0 18% 0)', transform:`translate(${x}px, ${x ? 0 : 22}px)` },
      { opacity:1, clipPath:'inset(0)', transform:'none' }
    ], { duration:950, delay, fill:'backwards' });
  }

  function reveal(target, kind, delay) {
    if (kind === 'project-photo' || kind === 'project-copy') {
      const row = target.closest('.al-project-row');
      const media = row.querySelector('.al-project-row-media');
      const copy = row.querySelector('.al-project-row-copy');
      const direction = desktop.matches && media && copy
        ? (media.getBoundingClientRect().left > copy.getBoundingClientRect().left ? 1 : -1) : 0;
      if (kind === 'project-photo') revealPhoto(media, delay, direction * 28);
      else copy.querySelectorAll(':scope > *').forEach((element, index) =>
        rise(element, delay + index * 65, direction * -16));
    } else if (kind === 'photo') {
      revealPhoto(target, delay);
    } else if (kind === 'copy') {
      const children = Array.from(target.children).filter(element =>
        element.matches('h1,h2,h3,p,.al-lead,.al-note,.al-text-link,.al-outline-link,div,span,header'));
      if (children.length) children.forEach((element, index) => rise(element, delay + Math.min(index * 70, 280)));
      else rise(target, delay);
    } else {
      rise(target, delay);
    }
  }

  function setupScroll(scope) {
    if (!('IntersectionObserver' in window)) return;
    observer ||= new IntersectionObserver(entries => {
      const visible = entries.filter(entry => entry.isIntersecting && !played.has(entry.target))
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top ||
          a.boundingClientRect.left - b.boundingClientRect.left);
      let rowTop = -Infinity;
      let column = 0;
      visible.forEach(({ target, boundingClientRect }) => {
        played.add(target);
        observer.unobserve(target);
        // Stagger neighbours on the same row; never accumulate delays down a long page.
        if (Math.abs(boundingClientRect.top - rowTop) > 48) column = 0;
        rowTop = boundingClientRect.top;
        reveal(target, effects.get(target), Math.min(column++ * 90, 180));
      });
    }, { threshold:.08, rootMargin:'0px 0px -28px 0px' });

    const watch = (selector, kind) => scope.querySelectorAll(selector).forEach(element => {
      if (played.has(element) || element.closest('.scroll-trigger')) return;
      effects.set(element, kind);
      observer.observe(element);
    });
    // Media and copy have independent triggers, especially when stacked on mobile.
    watch('.al-project-row-media', 'project-photo');
    watch('.al-project-row-copy', 'project-copy');
    watch('.al-home-selection > figure img,.al-project-photo-grid > figure img,' +
      '.al-about-photos > figure img,.al-print-hero img', 'photo');
    watch('.al-home-personal > figure,.al-about-portrait,.al-project-cover,' +
      '.al-print-presentation,.al-demand-photo,' +
      '.product__media-item:not(:first-child) .product__media', 'photo');
    // The shared frame preserves the photograph/mockup crossfade inside it.
    watch('.al-print-image', 'photo');
    watch('.al-home-statement,.al-home-personal > div,.al-about-intro > header,' +
      '.al-about-details > div,.al-index-hero,.al-project-heading,.al-print-intro-copy,' +
      '.al-compact-hero,.al-demand-heading,.footer-block__details-content', 'copy');
    watch('.al-section-heading,.al-portfolio-group-title,.al-project-film > h2,' +
      '.al-project-page > .al-wrap > h2,.al-project-film > iframe,.al-project-film > video,' +
      '.al-project-footer,.al-print-selection-note,.al-print-collection > header,' +
      '.al-home-selection figcaption,.al-project-photo-grid figcaption,.al-print-hero figcaption,' +
      '.al-print-caption,.al-room-label,' +
      '.al-about-details > .al-thanks,.al-contact-line,.al-compact-head,' +
      '.al-comparison-column dl > div,.al-comparison-note,.al-compact-note,' +
      '.product__title > h1,.product__description > *,.footer-block__heading,' +
      '.footer__content-bottom,#product-grid > li,.related-products .grid > li', 'rise');
  }

  function setup(scope = document) {
    const send = scope.querySelector('.contact__button button[type="submit"]');
    if (send && !send.querySelector('.al-contact-envelope')) {
      send.classList.add('al-envelope-submit');
      const envelope = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      envelope.setAttribute('viewBox', '0 0 24 24');
      envelope.setAttribute('class', 'al-contact-envelope');
      envelope.setAttribute('width', '22');
      envelope.setAttribute('height', '22');
      envelope.setAttribute('aria-hidden', 'true');
      envelope.setAttribute('focusable', 'false');
      envelope.innerHTML = '<g fill="none" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9v11h18V9"/><path class="al-envelope-flap" d="m3 9 9 6 9-6Z"/><path d="m3 20 6-6m12 6-6-6"/></g>';
      send.append(envelope);
    }
    if (reduced.matches) return;
    scope.querySelectorAll('.al-hero-copy > *').forEach((element, index) => {
      if (played.has(element)) return;
      played.add(element);
      animate(element, [{ opacity:.25, transform:'translateY(12px)' }, { opacity:1, transform:'none' }],
        { duration:700, delay:index * 90, fill:'backwards' });
    });
    setupScroll(scope);
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element) || reduced.matches) return;
    const choice = event.target.closest('.al-compact-plan,.al-compact-format');
    if (!choice || choice.classList.contains('is-active')) return;
    const total = document.getElementById('al-clean-total');
    const previousTotal = total?.textContent;
    requestAnimationFrame(() => {
      if (choice.getAttribute('aria-pressed') !== 'true') return;
      const mark = choice.querySelector('.al-choice-mark');
      animate(mark, [{ transform:'scale(.65)' }, { transform:'scale(1.12)' }, { transform:'scale(1)' }], { duration:280 });
      if (total?.textContent !== previousTotal)
        animate(total, [{ opacity:.4, transform:'translateY(4px)' }, { opacity:1, transform:'none' }], { duration:260 });
    });
  }, true);

  document.addEventListener('change', event => {
    if (!(event.target instanceof Element) || !event.target.matches('.product-form__input input[type=radio]')) return;
    animate(event.target.labels?.[0], [{ transform:'scale(.975)' }, { transform:'scale(1)' }], { duration:220 });
  });

  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      observer?.disconnect();
      [...running].forEach(animation => animation.cancel());
    } else setup();
  });
  document.addEventListener('shopify:section:load', event => setup(event.target));
  // Dawn inserts related products and filtered collection results after the first render.
  const catalog = document.querySelector('product-recommendations,#ProductGridContainer');
  if (catalog) new MutationObserver(() => {
    if (!reduced.matches) setupScroll(catalog);
  }).observe(catalog, { childList:true, subtree:true });
  setup();
})();
